// Client-side mirror of internal/lifecycle/lifecycle.go — UI-only, to
// decide which lifecycle buttons/inputs to show. The server is the real
// gate (every one of these endpoints re-checks and 403s), this only avoids
// offering a designer/admin an action the API will reject. Keep this in
// sync with lifecycle.go if that package's rules change.
import type { Quotation, QuotationStatus } from '$lib/types';

type Q = Pick<Quotation, 'status' | 'created_by_id' | 'designer_priced'>;

export function preApproval(status: QuotationStatus): boolean {
  return status === 'draft' || status === 'revised' || status === 'submitted' || status === 'priced';
}

export function canEdit(status: QuotationStatus): boolean {
  return status === 'draft' || status === 'revised';
}

export function canSubmit(status: QuotationStatus): boolean {
  return status === 'draft' || status === 'revised';
}

// canPrice mirrors lifecycle.CanPrice — admin-only gate is enforced by the
// caller (priceQuotation is 403'd for non-admins regardless of status).
export function canPrice(status: QuotationStatus): boolean {
  return status === 'draft' || status === 'submitted' || status === 'revised';
}

export function canApprove(q: Q, userID: number | undefined, isAdmin: boolean): boolean {
  if (q.designer_priced) return manageableBy(q, userID, isAdmin) && preApproval(q.status);
  return manageableBy(q, userID, isAdmin) && q.status === 'priced';
}

export function canReturnForPricing(status: QuotationStatus): boolean {
  return status === 'priced';
}

export function canRevise(q: Q, userID: number | undefined, isAdmin: boolean): boolean {
  return (isAdmin || q.created_by_id === userID) && (q.status === 'submitted' || q.status === 'priced' || q.status === 'approved');
}

export function canEditBOQ(q: Q, userID: number | undefined, isAdmin: boolean): boolean {
  if (isAdmin) return preApproval(q.status);
  const owner = q.created_by_id === userID;
  if (owner && q.designer_priced) return preApproval(q.status);
  if (owner && q.status === 'submitted') return true;
  return canEdit(q.status);
}

export function manageableBy(q: Q, userID: number | undefined, isAdmin: boolean): boolean {
  if (isAdmin) return true;
  return q.designer_priced && q.created_by_id === userID;
}

export function pricingEditableBy(q: Q, isAdmin: boolean): boolean {
  if (isAdmin) return true;
  return canEdit(q.status);
}

// ponytail: one runnable self-check mirroring lifecycle_test.go's headline
// cases, instead of a full test file/runner for a UI-only mirror module.
function selfCheck() {
  const mdHotelSubmitted: Q = { status: 'submitted', created_by_id: 1, designer_priced: false };
  console.assert(!canApprove(mdHotelSubmitted, 1, false), 'owner cannot approve MD-priced quote before pricing');
  console.assert(canPrice('submitted'), 'admin can price a submitted quote');

  const selfServeDraft: Q = { status: 'draft', created_by_id: 1, designer_priced: true };
  console.assert(canApprove(selfServeDraft, 1, false), 'self-serve owner can approve straight from draft');
  console.assert(canEditBOQ(selfServeDraft, 1, false), 'self-serve owner can edit BOQ in draft');

  const submitted: Q = { status: 'submitted', created_by_id: 1, designer_priced: false };
  console.assert(canRevise(submitted, 1, false), 'owner can revise a submitted MD-priced quote');
  console.assert(!canRevise(submitted, 2, false), 'non-owner cannot revise a submitted quote');
}
if (import.meta.env.DEV) selfCheck();
